import React from 'react';
import {
  TextField,
  Checkbox,
  Radio,
  Select,
  Autocomplete,
  Button,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  InputAdornment,
  type TextFieldProps,
  type CheckboxProps,
  type RadioProps,
  type SelectProps,
  type AutocompleteProps,
  type ButtonProps,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HistoryIcon from '@mui/icons-material/History';
import ListIcon from '@mui/icons-material/List';
import UndoIcon from '@mui/icons-material/Undo';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import type { DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import type { TimePickerProps } from '@mui/x-date-pickers/TimePicker';

// ─── Types ────────────────────────────────────────────────────────────────────

export type NavFieldType = 'text' | 'checkbox' | 'radio' | 'number' | 'combobox' | 'date';

// Types where plain Arrow keys have their own meaning (change value / navigate list)
// — Ctrl+Arrow is used for field navigation instead.
const CTRL_ARROW_TYPES: NavFieldType[] = ['radio', 'number', 'combobox', 'date'];

// ─── Field options dropdown Menu ──────────────────────────────────────────────

export interface NavFieldMenuProps {
  onHistoryClick?: () => void;
  historyDisabled?: boolean;
  onEditTagListClick?: () => void;
  tagListTitle?: string;
  onResetClick?: () => void;
  resetDisabled?: boolean;
  hideMenu?: boolean;
}

export function NavFieldMenu({
  onHistoryClick,
  historyDisabled,
  onEditTagListClick,
  tagListTitle,
  onResetClick,
  resetDisabled,
  hideMenu,
}: NavFieldMenuProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const hasHistory = onHistoryClick !== undefined;
  const hasEditTag = onEditTagListClick !== undefined;
  const hasReset = onResetClick !== undefined;

  if (hideMenu || (!hasHistory && !hasEditTag && !hasReset)) return null;

  return (
    <>
      <InputAdornment position="start">
        <IconButton
          color="default"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleOpen}
          edge="start"
          size="small"
          sx={{ mr: 0.5, p: '4px' }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
        <Divider sx={{ height: 28, mx: 0.5 }} orientation="vertical" />
      </InputAdornment>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              minWidth: 180,
              borderRadius: '8px',
              mt: 0.5,
              '& .MuiMenuItem-root': {
                fontSize: '0.85rem',
                py: 1,
                px: 1.5,
              },
            },
          },
        }}
      >
        {hasHistory && (
          <MenuItem
            disabled={historyDisabled}
            onClick={() => {
              onHistoryClick();
              handleClose();
            }}
          >
            <ListItemIcon sx={{ minWidth: '32px !important' }}>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>History</ListItemText>
          </MenuItem>
        )}
        {hasEditTag && (
          <MenuItem
            onClick={() => {
              onEditTagListClick();
              handleClose();
            }}
          >
            <ListItemIcon sx={{ minWidth: '32px !important' }}>
              <ListIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{`Edit ${tagListTitle ?? 'Tag'} List`}</ListItemText>
          </MenuItem>
        )}
        {hasReset && (
          <MenuItem
            disabled={resetDisabled}
            onClick={() => {
              onResetClick();
              handleClose();
            }}
          >
            <ListItemIcon sx={{ minWidth: '32px !important' }}>
              <UndoIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Reset</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface FormNavigationContextValue {
  containerRef: React.RefObject<HTMLElement | null>;
  /** Optional — only needed when the submit button is NOT a NavButton in the field list. */
  submitButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

const FormNavigationContext = React.createContext<FormNavigationContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

interface FormNavigationProviderProps {
  children: React.ReactNode;
  containerRef: React.RefObject<HTMLElement | null>;
  /** Optional — only needed when the submit button is NOT a NavButton in the field list. */
  submitButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

export function FormNavigationProvider({
  children,
  containerRef,
  submitButtonRef,
}: FormNavigationProviderProps) {
  const value = React.useMemo(
    () => ({ containerRef, submitButtonRef }),
    [containerRef, submitButtonRef],
  );
  return (
    <FormNavigationContext.Provider value={value}>
      {children}
    </FormNavigationContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useFormNavigation(fieldType: NavFieldType = 'text') {
  const ctx = React.useContext(FormNavigationContext);

  const navigate = React.useCallback(
    (direction: 'next' | 'prev', currentElement: HTMLElement) => {
      if (!ctx) return;
      const container = ctx.containerRef.current;
      if (!container) return;

      // [data-nav-field]:not([data-nav-skip]) — skipped fields are invisible.
      const fields = Array.from(
        container.querySelectorAll<HTMLElement>('[data-nav-field]:not([data-nav-skip])'),
      );
      if (fields.length === 0) return;

      const idx = fields.indexOf(currentElement);
      if (idx === -1) return;

      if (direction === 'next') {
        if (idx < fields.length - 1) {
          fields[idx + 1].focus();
        } else {
          // Fallback for forms that don't use NavButton as last item.
          ctx.submitButtonRef?.current?.focus();
        }
      } else {
        if (idx > 0) fields[idx - 1].focus();
      }
    },
    [ctx],
  );

  const handleKeyUp = React.useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      // Ctrl+Enter is reserved for "open picker/dropdown" in each component — skip here.
      if (e.key === 'Enter' && e.ctrlKey) return;

      const useCtrlArrow = CTRL_ARROW_TYPES.includes(fieldType);
      const target = e.currentTarget as HTMLElement;

      if (e.key === 'Enter') {
        navigate('next', target);
      } else if (!useCtrlArrow && e.key === 'ArrowDown') {
        navigate('next', target);
      } else if (!useCtrlArrow && e.key === 'ArrowUp') {
        navigate('prev', target);
      } else if (useCtrlArrow && e.ctrlKey && e.key === 'ArrowDown') {
        navigate('next', target);
      } else if (useCtrlArrow && e.ctrlKey && e.key === 'ArrowUp') {
        navigate('prev', target);
      }
    },
    [navigate, fieldType],
  );

  return { handleKeyUp, navigate };
}

// ─── NavTextField ─────────────────────────────────────────────────────────────
// Use navFieldType="number" for numeric inputs.
//
// Why inputRef instead of e.currentTarget:
// MUI TextField's onKeyDown fires on the MuiInputBase-root <div>, not the
// inner <input> that carries data-nav-field. navigate() would get idx === -1
// and land on the wrong field. Using inputRef (which points to the actual
// <input>) fixes the lookup — same pattern as NavCheckbox / NavRadio.

type NavTextFieldProps = TextFieldProps & NavFieldMenuProps & {
  navFieldType?: NavFieldType;
  navSkip?: boolean;
  allowDualValue?: boolean;
  secondaryValue?: string;
  onSecondaryChange?: (value: string) => void;
};

export function NavTextField({
  navFieldType = 'text',
  navSkip = false,
  inputProps,
  inputRef: externalInputRef,
  onKeyDown,
  onKeyUp,
  onChange,
  onBlur: externalOnBlur,
  allowDualValue,
  secondaryValue,
  onSecondaryChange,
  value,
  sx,
  InputProps,
  slotProps,
  onHistoryClick,
  historyDisabled,
  onEditTagListClick,
  tagListTitle,
  onResetClick,
  resetDisabled,
  hideMenu,
  ...rest
}: NavTextFieldProps & { inputRef?: React.Ref<HTMLInputElement> }) {
  const { navigate } = useFormNavigation(navFieldType);
  const internalRef = React.useRef<HTMLInputElement>(null);
  const [isSecondaryActive, setIsSecondaryActive] = React.useState(false);

  React.useEffect(() => {
    if (!allowDualValue) {
      setIsSecondaryActive(false);
    }
  }, [allowDualValue]);

  // Merge external ref (if caller passes one) with our internal ref.
  const setRef = React.useCallback(
    (el: HTMLInputElement | null) => {
      (internalRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
      if (typeof externalInputRef === 'function') externalInputRef(el);
      else if (externalInputRef && 'current' in externalInputRef)
        (externalInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
    },
    [externalInputRef],
  );

  const isNumber = navFieldType === 'number';
  const displayValue = allowDualValue && isSecondaryActive ? (secondaryValue ?? '') : value;

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      if (allowDualValue && isSecondaryActive) {
        onSecondaryChange?.(v);
      } else {
        onChange?.(e);
      }
    },
    [onChange, onSecondaryChange, allowDualValue, isSecondaryActive],
  );

  const fieldMenu = (
    <NavFieldMenu
      onHistoryClick={onHistoryClick}
      historyDisabled={historyDisabled}
      onEditTagListClick={onEditTagListClick}
      tagListTitle={tagListTitle}
      onResetClick={onResetClick}
      resetDisabled={resetDisabled}
      hideMenu={hideMenu}
    />
  );

  return (
    <TextField
      {...rest}
      autoComplete="off"
      value={displayValue}
      inputRef={setRef}
      slotProps={{
        ...slotProps,
        input: {
          ...(InputProps as object),
          ...(slotProps?.input as object),
          startAdornment: (
            <>
              {fieldMenu}
              {(InputProps as any)?.startAdornment}
              {(slotProps?.input as any)?.startAdornment}
            </>
          ),
          style: isNumber ? {
            ...((slotProps?.input as any)?.style ?? {}),
          } : (slotProps?.input as any)?.style,
        },
      }}
      sx={{
        ...(isNumber && {
          '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          '& input[type="number"]': {
            MozAppearance: 'textfield',
          },
        }),
        ...(allowDualValue && isSecondaryActive && {
          '& .MuiInputBase-root': { backgroundColor: '#fffde7' },
          '& .MuiInputBase-input': { fontStyle: 'italic' },
        }),
        ...(sx as object),
      }}
      inputProps={{
        ...inputProps,
        'data-nav-field': navFieldType,
        ...(navSkip && { 'data-nav-skip': '' }),
      }}
      onChange={handleChange}
      onKeyDown={(e) => {
        // Prevent page scroll on ArrowUp/Down for non-number fields.
        if (!navSkip && !isNumber && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
          e.preventDefault();
        }
        onKeyDown?.(e);
      }}
      onKeyUp={(e) => {
        if (allowDualValue && e.key === 'F8') {
          e.preventDefault();
          setIsSecondaryActive(prev => !prev);
          return;
        }
        if (!navSkip) {
          if (e.key === 'Enter' && !e.ctrlKey) {
            navigate('next', internalRef.current!);
          } else if (!isNumber && e.key === 'ArrowDown') {
            navigate('next', internalRef.current!);
          } else if (!isNumber && e.key === 'ArrowUp') {
            navigate('prev', internalRef.current!);
          } else if (isNumber && e.ctrlKey && e.key === 'ArrowDown') {
            navigate('next', internalRef.current!);
          } else if (isNumber && e.ctrlKey && e.key === 'ArrowUp') {
            navigate('prev', internalRef.current!);
          }
        }
        onKeyUp?.(e);
      }}
      onBlur={(e) => {
        setIsSecondaryActive(false);
        externalOnBlur?.(e);
      }}
    />
  );
}

// ─── NavLongField ─────────────────────────────────────────────────────────
// Custom number input for Long fields: digits and minus (at beginning only).
// Blocks: 'e', 'E', '+', '.' (no decimals, no scientific notation)

type NavLongFieldProps = Omit<TextFieldProps, 'onChange' | 'type'> & NavFieldMenuProps & {
  navFieldType?: 'number';
  navSkip?: boolean;
  onChange: (value: string) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  allowDualValue?: boolean;
  secondaryValue?: string;
  onSecondaryChange?: (value: string) => void;
  onSecondaryModeChange?: (active: boolean) => void;
};

export function NavLongField({
  navFieldType = 'number',
  navSkip = false,
  onChange,
  inputProps,
  inputRef: externalInputRef,
  onKeyDown,
  onKeyUp,
  onBlur: externalOnBlur,
  sx,
  allowDualValue,
  secondaryValue,
  onSecondaryChange,
  onSecondaryModeChange,
  value,
  onHistoryClick,
  historyDisabled,
  onEditTagListClick,
  tagListTitle,
  onResetClick,
  resetDisabled,
  hideMenu,
  ...rest
}: NavLongFieldProps & { value: string }) {
  const { navigate } = useFormNavigation(navFieldType);
  const internalRef = React.useRef<HTMLInputElement>(null);
  const [isSecondaryActive, setIsSecondaryActive] = React.useState(false);

  React.useEffect(() => {
    if (!allowDualValue) {
      setIsSecondaryActive(false);
    }
  }, [allowDualValue]);

  React.useEffect(() => {
    onSecondaryModeChange?.(isSecondaryActive);
  }, [isSecondaryActive, onSecondaryModeChange]);

  const setRef = React.useCallback(
    (el: HTMLInputElement | null) => {
      (internalRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
      if (typeof externalInputRef === 'function') externalInputRef(el);
      else if (externalInputRef && 'current' in externalInputRef)
        (externalInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
    },
    [externalInputRef],
  );

  const displayValue = allowDualValue && isSecondaryActive ? (secondaryValue ?? '') : value;

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let v = e.target.value;
      const pattern = /^-?\d*$/;
      if (v !== '' && !pattern.test(v)) return;
      if (v.includes('-') && !v.startsWith('-')) return;
      if ((v.match(/-/g) || []).length > 1) return;
      if (allowDualValue && isSecondaryActive) {
        onSecondaryChange?.(v);
      } else {
        onChange(v);
      }
    },
    [onChange, onSecondaryChange, allowDualValue, isSecondaryActive],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '.') {
        e.preventDefault();
        return;
      }
      if (e.key === '-') {
        const input = e.currentTarget as HTMLInputElement;
        const currentValue = input.value;
        const hasMinus = currentValue.includes('-');
        const cursorAtStart = input.selectionStart === 0;
        if (hasMinus || (!cursorAtStart && currentValue !== '')) {
          e.preventDefault();
          return;
        }
      }
      if (!navSkip && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
      }
      onKeyDown?.(e);
    },
    [navSkip, onKeyDown],
  );

  const handleKeyUp = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (allowDualValue && e.key === 'F8') {
        e.preventDefault();
        setIsSecondaryActive(prev => !prev);
        return;
      }
      if (!navSkip) {
        if (e.key === 'Enter' && !e.ctrlKey) {
          navigate('next', internalRef.current!);
        } else if (e.key === 'ArrowDown') {
          navigate('next', internalRef.current!);
        } else if (e.key === 'ArrowUp') {
          navigate('prev', internalRef.current!);
        }
      }
      onKeyUp?.(e);
    },
    [navigate, navSkip, onKeyUp, allowDualValue],
  );

  const fieldMenu = (
    <NavFieldMenu
      onHistoryClick={onHistoryClick}
      historyDisabled={historyDisabled}
      onEditTagListClick={onEditTagListClick}
      tagListTitle={tagListTitle}
      onResetClick={onResetClick}
      resetDisabled={resetDisabled}
      hideMenu={hideMenu}
    />
  );

  return (
    <TextField
      {...rest}
      autoComplete="off"
      type="number"
      value={displayValue}
      inputRef={setRef}
      inputProps={{
        ...inputProps,
        'data-nav-field': navFieldType,
        ...(navSkip && { 'data-nav-skip': '' }),
      }}
      InputProps={{
        ...(rest.InputProps as object),
        startAdornment: (
          <>
            {fieldMenu}
            {(rest.InputProps as any)?.startAdornment}
          </>
        ),
      }}
      sx={{
        '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
        '& input[type="number"]': {
          MozAppearance: 'textfield',
        },
        ...(allowDualValue && isSecondaryActive && {
          '& .MuiInputBase-root': { backgroundColor: '#fffde7' },
          '& .MuiInputBase-input': { fontStyle: 'italic' },
        }),
        ...(sx as object),
      }}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
    />
  );
}

// ─── NavDoubleField ───────────────────────────────────────────────────────
// Custom number input for Double fields: digits, decimal point/comma, minus (at beginning only).
// Blocks: 'e', 'E', '+' (no scientific notation)

type NavDoubleFieldProps = Omit<TextFieldProps, 'onChange' | 'type'> & NavFieldMenuProps & {
  navFieldType?: 'number';
  navSkip?: boolean;
  onChange: (value: string) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  allowDualValue?: boolean;
  secondaryValue?: string;
  onSecondaryChange?: (value: string) => void;
  onSecondaryModeChange?: (active: boolean) => void;
};

export function NavDoubleField({
  navFieldType = 'number',
  navSkip = false,
  onChange,
  inputProps,
  inputRef: externalInputRef,
  onKeyDown,
  onKeyUp,
  onBlur: externalOnBlur,
  sx,
  allowDualValue,
  secondaryValue,
  onSecondaryChange,
  onSecondaryModeChange,
  value,
  onHistoryClick,
  historyDisabled,
  onEditTagListClick,
  tagListTitle,
  onResetClick,
  resetDisabled,
  hideMenu,
  ...rest
}: NavDoubleFieldProps & { value: string }) {
  const { navigate } = useFormNavigation(navFieldType);
  const internalRef = React.useRef<HTMLInputElement>(null);
  const [isSecondaryActive, setIsSecondaryActive] = React.useState(false);

  React.useEffect(() => {
    if (!allowDualValue) {
      setIsSecondaryActive(false);
    }
  }, [allowDualValue]);

  React.useEffect(() => {
    onSecondaryModeChange?.(isSecondaryActive);
  }, [isSecondaryActive, onSecondaryModeChange]);

  const setRef = React.useCallback(
    (el: HTMLInputElement | null) => {
      (internalRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
      if (typeof externalInputRef === 'function') externalInputRef(el);
      else if (externalInputRef && 'current' in externalInputRef)
        (externalInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
    },
    [externalInputRef],
  );

  const displayValue = allowDualValue && isSecondaryActive ? (secondaryValue ?? '') : value;

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let v = e.target.value;
      const pattern = /^-?\d*[.,]?\d*$/;
      if (v !== '' && !pattern.test(v)) return;
      if (v.includes('-') && !v.startsWith('-')) return;
      if ((v.match(/-/g) || []).length > 1) return;
      v = v.replace(',', '.');
      if (allowDualValue && isSecondaryActive) {
        onSecondaryChange?.(v);
      } else {
        onChange(v);
      }
    },
    [onChange, onSecondaryChange, allowDualValue, isSecondaryActive],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'e' || e.key === 'E' || e.key === '+') {
        e.preventDefault();
        return;
      }
      if (e.key === '-') {
        const input = e.currentTarget as HTMLInputElement;
        const currentValue = input.value;
        const hasMinus = currentValue.includes('-');
        const cursorAtStart = input.selectionStart === 0;
        if (hasMinus || (!cursorAtStart && currentValue !== '')) {
          e.preventDefault();
          return;
        }
      }
      if (!navSkip && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
      }
      onKeyDown?.(e);
    },
    [navSkip, onKeyDown],
  );

  const handleKeyUp = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (allowDualValue && e.key === 'F8') {
        e.preventDefault();
        setIsSecondaryActive(prev => !prev);
        return;
      }
      if (!navSkip) {
        if (e.key === 'Enter' && !e.ctrlKey) {
          navigate('next', internalRef.current!);
        } else if (e.key === 'ArrowDown') {
          navigate('next', internalRef.current!);
        } else if (e.key === 'ArrowUp') {
          navigate('prev', internalRef.current!);
        }
      }
      onKeyUp?.(e);
    },
    [navigate, navSkip, onKeyUp, allowDualValue],
  );

  const fieldMenu = (
    <NavFieldMenu
      onHistoryClick={onHistoryClick}
      historyDisabled={historyDisabled}
      onEditTagListClick={onEditTagListClick}
      tagListTitle={tagListTitle}
      onResetClick={onResetClick}
      resetDisabled={resetDisabled}
      hideMenu={hideMenu}
    />
  );

  return (
    <TextField
      {...rest}
      autoComplete="off"
      type="number"
      value={displayValue}
      inputRef={setRef}
      inputProps={{
        ...inputProps,
        'data-nav-field': navFieldType,
        ...(navSkip && { 'data-nav-skip': '' }),
      }}
      InputProps={{
        ...(rest.InputProps as object),
        startAdornment: (
          <>
            {fieldMenu}
            {(rest.InputProps as any)?.startAdornment}
          </>
        ),
      }}
      sx={{
        '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
        '& input[type="number"]': {
          MozAppearance: 'textfield',
        },
        ...(allowDualValue && isSecondaryActive && {
          '& .MuiInputBase-root': { backgroundColor: '#fffde7' },
          '& .MuiInputBase-input': { fontStyle: 'italic' },
        }),
        ...(sx as object),
      }}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onBlur={(e) => {
        setIsSecondaryActive(false);
        externalOnBlur?.(e);
      }}
    />
  );
}

// ─── NavCheckbox ──────────────────────────────────────────────────────────────
// Space is not intercepted — toggles the checkbox normally.
//
// Why inputRef instead of e.currentTarget:
// MUI Checkbox is built on ButtonBase (<span>). onKeyDown fires on the <span>,
// so e.currentTarget is the <span> — NOT the <input type="checkbox"> that carries
// data-nav-field. navigate() would get idx === -1 and land on the wrong field.
// Using inputRef (which points to the actual <input>) fixes the lookup.
// e.preventDefault() on Enter stops ButtonBase from firing a synthetic click().

export function NavCheckbox({
  onKeyDown,
  inputProps,
  navSkip = false,
  ...rest
}: CheckboxProps & { navSkip?: boolean }) {
  const { navigate } = useFormNavigation('checkbox');
  const inputRef = React.useRef<HTMLInputElement>(null);
  return (
    <Checkbox
      {...rest}
      inputRef={inputRef}
      inputProps={{
        ...inputProps,
        'data-nav-field': 'checkbox',
        ...(navSkip && { 'data-nav-skip': '' }),
      } as React.InputHTMLAttributes<HTMLInputElement>}
      onKeyDown={(e) => {
        if (!navSkip && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
          e.preventDefault(); // prevent page scroll on keydown
        }
        // Space: not intercepted — toggles checkbox normally
        onKeyDown?.(e);
      }}
      onKeyUp={(e) => {
        if (!navSkip) {
          if (e.key === 'Enter') {
            e.preventDefault(); // stop ButtonBase synthetic click on keyup
            if (inputRef.current) navigate('next', inputRef.current);
          } else if (e.key === 'ArrowDown') {
            if (inputRef.current) navigate('next', inputRef.current);
          } else if (e.key === 'ArrowUp') {
            if (inputRef.current) navigate('prev', inputRef.current);
          }
        }
      }}
    />
  );
}

// ─── NavRadio ─────────────────────────────────────────────────────────────────
// Plain Arrow cycles within radio group (browser default, not intercepted).
// Ctrl+Arrow navigates to next/prev field. Space selects (not intercepted).
// Same e.currentTarget / inputRef fix as NavCheckbox above.

export function NavRadio({
  onKeyDown,
  inputProps,
  navSkip = false,
  ...rest
}: RadioProps & { navSkip?: boolean }) {
  const { navigate } = useFormNavigation('radio');
  const inputRef = React.useRef<HTMLInputElement>(null);
  return (
    <Radio
      {...rest}
      inputRef={inputRef}
      inputProps={{
        ...inputProps,
        'data-nav-field': 'radio',
        ...(navSkip && { 'data-nav-skip': '' }),
      } as React.InputHTMLAttributes<HTMLInputElement>}
      onKeyDown={(e) => {
        // Plain Arrow: not intercepted — cycles within radio group (browser default)
        // Space: not intercepted — selects radio normally
        onKeyDown?.(e);
      }}
      onKeyUp={(e) => {
        if (!navSkip) {
          if (e.key === 'Enter') {
            e.preventDefault(); // stop ButtonBase synthetic click on keyup
            if (inputRef.current) navigate('next', inputRef.current);
          } else if (e.ctrlKey && e.key === 'ArrowDown') {
            if (inputRef.current) navigate('next', inputRef.current);
          } else if (e.ctrlKey && e.key === 'ArrowUp') {
            if (inputRef.current) navigate('prev', inputRef.current);
          }
        }
      }}
    />
  );
}

// ─── NavSelect ────────────────────────────────────────────────────────────────
// Enter navigates (prevents dropdown from opening).
// Ctrl+Enter opens the dropdown.

export function NavSelect({
  SelectDisplayProps: sdp,
  children,
  open: externalOpen,
  onOpen,
  onClose,
  onHistoryClick,
  historyDisabled,
  onEditTagListClick,
  tagListTitle,
  onResetClick,
  resetDisabled,
  hideMenu,
  ...rest
}: SelectProps & NavFieldMenuProps) {
  const { handleKeyUp } = useFormNavigation('combobox');
  const [open, setOpen] = React.useState(false);

  const isControlled = externalOpen !== undefined;
  const isOpen = isControlled ? externalOpen : open;

  const fieldMenu = (
    <NavFieldMenu
      onHistoryClick={onHistoryClick}
      historyDisabled={historyDisabled}
      onEditTagListClick={onEditTagListClick}
      tagListTitle={tagListTitle}
      onResetClick={onResetClick}
      resetDisabled={resetDisabled}
      hideMenu={hideMenu}
    />
  );

  return (
    <Select
      {...rest}
      open={isOpen}
      onOpen={(e) => { if (!isControlled) setOpen(true); onOpen?.(e); }}
      onClose={(e) => { if (!isControlled) setOpen(false); onClose?.(e); }}
      startAdornment={fieldMenu || rest.startAdornment}
      SelectDisplayProps={{
        ...(sdp as object),
        'data-nav-field': 'combobox',
        onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
          if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault();
            if (!isControlled) setOpen(true);
            return;
          }
          // Prevent MUI from opening the dropdown on plain Enter.
          if (e.key === 'Enter') e.preventDefault();
          (sdp as any)?.onKeyDown?.(e);
        },
        onKeyUp: (e: React.KeyboardEvent<HTMLElement>) => {
          handleKeyUp(e);
          (sdp as any)?.onKeyUp?.(e);
        },
      } as React.HTMLAttributes<HTMLDivElement>}
    >
      {children}
    </Select>
  );
}

// ─── NavAutocomplete ──────────────────────────────────────────────────────────
// Enter prevents dropdown open and navigates.
// Ctrl+Enter opens the dropdown.

export function NavAutocomplete<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  renderInput,
  open: externalOpen,
  onOpen,
  onClose,
  onHighlightChange,
  onHistoryClick,
  historyDisabled,
  onEditTagListClick,
  tagListTitle,
  onResetClick,
  resetDisabled,
  hideMenu,
  ...rest
}: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo> & NavFieldMenuProps) {
  const { navigate } = useFormNavigation('combobox');
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const highlightedRef = React.useRef<Value | null>(null);

  const isControlled = externalOpen !== undefined;
  const isOpen = isControlled ? externalOpen : open;

  const fieldMenu = (
    <NavFieldMenu
      onHistoryClick={onHistoryClick}
      historyDisabled={historyDisabled}
      onEditTagListClick={onEditTagListClick}
      tagListTitle={tagListTitle}
      onResetClick={onResetClick}
      resetDisabled={resetDisabled}
      hideMenu={hideMenu}
    />
  );

  return (
    <Autocomplete
      {...rest}
      open={isOpen}
      onOpen={(e) => { if (!isControlled) setOpen(true); onOpen?.(e); }}
      onClose={(e, reason) => { if (!isControlled) setOpen(false); onClose?.(e, reason); }}
      onHighlightChange={(e, option, reason) => {
        highlightedRef.current = option as Value | null;
        onHighlightChange?.(e, option, reason);
      }}
      renderInput={(params) => {
        const original = renderInput(params) as React.ReactElement<any>;
        return React.cloneElement(original, {
          inputRef: (el: HTMLInputElement | null) => {
            (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
            const origRef = (params as any).inputRef;
            if (typeof origRef === 'function') origRef(el);
            else if (origRef && 'current' in origRef) origRef.current = el;
          },
          autoComplete: 'off',
          inputProps: {
            ...(params.inputProps as object),
            'data-nav-field': 'combobox',
            autoComplete: 'off',
          },
          InputProps: {
            ...(original.props?.InputProps as object),
            startAdornment: (
              <>
                {fieldMenu}
                {(original.props?.InputProps as any)?.startAdornment}
              </>
            ),
          },
          onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
            // Ctrl+Enter: immediately close dropdown and navigate.
            if (e.key === 'Enter' && e.ctrlKey) {
              e.preventDefault();
              e.stopPropagation();
              if (isOpen && !isControlled) setOpen(false);
              if (inputRef.current) navigate('next', inputRef.current);
              return;
            }
            // Space: toggles highlighted option in multi-select (must be keydown for preventDefault to work).
            if (e.key === ' ' && isOpen && rest.multiple && highlightedRef.current !== null) {
              e.preventDefault();
              const highlighted = highlightedRef.current;
              const currentValue = ((rest.value as Value[] | undefined) ?? []);
              const eq = rest.isOptionEqualToValue ?? ((a: Value, b: Value) => a === b);
              const isSelected = currentValue.some((v) => eq(v, highlighted));
              const newValue = isSelected
                ? currentValue.filter((v) => !eq(v, highlighted))
                : [...currentValue, highlighted];
              (rest.onChange as any)?.(e, newValue, isSelected ? 'removeOption' : 'selectOption', { option: highlighted });
              return;
            }
            // Ctrl+Arrow: stop MUI from navigating the dropdown on keydown.
            if (e.ctrlKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            (original as any).props?.onKeyDown?.(e);
          },
          onKeyUp: (e: React.KeyboardEvent<HTMLElement>) => {
            if (!inputRef.current) return;
            if (e.key === 'Enter' && !e.ctrlKey) {
              if (!isOpen) {
                const hasValue = rest.multiple
                  ? Array.isArray(rest.value) && rest.value.length > 0
                  : (rest.value !== undefined && rest.value !== null && rest.value !== '') || inputRef.current.value !== '';
                if (hasValue) {
                  navigate('next', inputRef.current);
                } else {
                  if (!isControlled) setOpen(true);
                }
              } else {
                // Dropdown open: MUI already selected/deselected on keydown.
                // Close if still open (e.g. multiple select with no highlighted).
                if (!isControlled) setOpen(false);
              }
            } else if (e.ctrlKey && e.key === 'ArrowDown') {
              navigate('next', inputRef.current);
            } else if (e.ctrlKey && e.key === 'ArrowUp') {
              navigate('prev', inputRef.current);
            }
          },
        });
      }}
    />
  );
}

// ─── NavTimePicker ────────────────────────────────────────────────────────────
// Enter navigates (TimePicker does NOT open on Enter, so no preventDefault needed).
// Ctrl+Enter opens the time picker.

export function NavTimePicker({ slotProps, ...rest }: TimePickerProps<any>) {
  const { navigate } = useFormNavigation('date');
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const mergedSlotProps = { ...slotProps, textField: { ...(slotProps as any)?.textField, autoComplete: 'off' } };

  // MUI x-date-pickers v8 uses PickersSectionList (not a real <input>).
  // PickersSectionList does not forward arbitrary props to the DOM, so
  // data-nav-field on inputProps never reaches a DOM node. Instead we own a
  // wrapper div that holds data-nav-field, catches bubbled keyboard events, and
  // forwards programmatic focus (from navigate()) to the inner [tabindex="0"].
  return (
    <div
      ref={wrapperRef}
      data-nav-field="date"
      tabIndex={-1}
      style={{ outline: 'none' }}
      onFocus={(e) => {
        if (e.target === wrapperRef.current) {
          wrapperRef.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.ctrlKey) { e.preventDefault(); setOpen(true); }
      }}
      onKeyUp={(e) => {
        if (!wrapperRef.current) return;
        if (e.key === 'Enter' && !e.ctrlKey) {
          navigate('next', wrapperRef.current);
        } else if (e.ctrlKey && e.key === 'ArrowDown') {
          navigate('next', wrapperRef.current);
        } else if (e.ctrlKey && e.key === 'ArrowUp') {
          navigate('prev', wrapperRef.current);
        }
      }}
    >
      <TimePicker
        {...rest}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        slotProps={mergedSlotProps}
      />
    </div>
  );
}

// ─── NavDatePicker ────────────────────────────────────────────────────────────
// Same behaviour as NavTimePicker.

export function NavDatePicker({
  slotProps,
  onHistoryClick,
  historyDisabled,
  onEditTagListClick,
  tagListTitle,
  onResetClick,
  resetDisabled,
  hideMenu,
  ...rest
}: DatePickerProps<any> & NavFieldMenuProps) {
  const { navigate } = useFormNavigation('date');
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const fieldMenu = (
    <NavFieldMenu
      onHistoryClick={onHistoryClick}
      historyDisabled={historyDisabled}
      onEditTagListClick={onEditTagListClick}
      tagListTitle={tagListTitle}
      onResetClick={onResetClick}
      resetDisabled={resetDisabled}
      hideMenu={hideMenu}
    />
  );

  const mergedSlotProps = {
    ...slotProps,
    textField: {
      ...(slotProps as any)?.textField,
      autoComplete: 'off',
      InputProps: {
        ...((slotProps as any)?.textField?.InputProps as object),
        startAdornment: (
          <>
            {fieldMenu}
            {((slotProps as any)?.textField?.InputProps as any)?.startAdornment}
          </>
        ),
      },
    },
  };

  // MUI x-date-pickers v8 uses PickersSectionList (not a real <input>).
  // PickersSectionList does not forward arbitrary props to the DOM, so
  // data-nav-field on inputProps never reaches a DOM node. Instead we own a
  // wrapper div that holds data-nav-field, catches bubbled keyboard events, and
  // forwards programmatic focus (from navigate()) to the inner [tabindex="0"].
  return (
    <div
      ref={wrapperRef}
      data-nav-field="date"
      tabIndex={-1}
      style={{ outline: 'none' }}
      onFocus={(e) => {
        if (e.target === wrapperRef.current) {
          wrapperRef.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.ctrlKey) { e.preventDefault(); setOpen(true); }
      }}
      onKeyUp={(e) => {
        if (!wrapperRef.current) return;
        if (e.key === 'Enter' && !e.ctrlKey) {
          navigate('next', wrapperRef.current);
        } else if (e.ctrlKey && e.key === 'ArrowDown') {
          navigate('next', wrapperRef.current);
        } else if (e.ctrlKey && e.key === 'ArrowUp') {
          navigate('prev', wrapperRef.current);
        }
      }}
    >
      <DatePicker
        {...rest}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        slotProps={mergedSlotProps}
      />
    </div>
  );
}

// ─── NavButton ────────────────────────────────────────────────────────────────
// NavButton IS part of the navigation circulation: data-nav-field="button" makes
// it visible to navigate() so Enter/Arrow on the previous field lands here.
//
// navSkip=true (default): the button does NOT intercept nav keys itself —
// Enter fires the button naturally (MUI Button default behaviour). The button
// is a terminal node: you navigate TO it, not THROUGH it.
//
// navSkip=false: Enter on the button also navigates forward (useful when a
// button sits mid-sequence in a multi-step form).

interface NavButtonProps extends ButtonProps {
  navSkip?: boolean;
}

export const NavButton = React.forwardRef<HTMLButtonElement, NavButtonProps>(
  function NavButton({ navSkip = true, children, ...rest }, forwardedRef) {
    const { navigate } = useFormNavigation('text');
    const internalRef = React.useRef<HTMLButtonElement>(null);

    const setRef = React.useCallback(
      (el: HTMLButtonElement | null) => {
        (internalRef as React.MutableRefObject<HTMLButtonElement | null>).current = el;
        if (typeof forwardedRef === 'function') forwardedRef(el);
        else if (forwardedRef && 'current' in forwardedRef)
          (forwardedRef as React.MutableRefObject<HTMLButtonElement | null>).current = el;
      },
      [forwardedRef],
    );

    return (
      <Button
        {...rest}
        ref={setRef}
        // data-nav-field on the <button> element puts it in the nav circulation.
        // data-nav-skip is intentionally NOT set — the button IS reachable via
        // Enter/Arrow from the previous nav field.
        {...{ 'data-nav-field': 'button' } as any}
        onKeyDown={(e) => {
          rest.onKeyDown?.(e as any);
        }}
        onKeyUp={(e) => {
          const ke = e as React.KeyboardEvent<HTMLElement>;
          if (ke.key === 'ArrowUp' && internalRef.current) {
            navigate('prev', internalRef.current);
          } else if (!navSkip && ke.key === 'Enter' && !ke.ctrlKey && internalRef.current) {
            navigate('next', internalRef.current);
          }
        }}
      >
        {children}
      </Button>
    );
  },
);
